import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import './styles/design-system.css';
import StoryContainer from './components/StoryContainer';
import SudokuPage from './pages/SudokuPage';
import { FiDownload, FiTerminal, FiChevronUp, FiMenu, FiX, FiBookOpen, FiCode, FiHeart, FiMail } from 'react-icons/fi';

const getInitialRoute = () => {
  if (typeof window === 'undefined') return 'home';
  const path = window.location.pathname.toLowerCase();
  const search = window.location.search.toLowerCase();
  const hash = window.location.hash.toLowerCase();
  if (
    path === '/sudoku' ||
    path.startsWith('/sudoku/') ||
    path === '/games/sudoku' ||
    search.includes('page=sudoku') ||
    hash === '#sudoku'
  ) {
    return 'sudoku';
  }
  return 'home';
};

function App() {
  const [currentRoute, setCurrentRoute] = useState(getInitialRoute);
  const [navScrolled, setNavScrolled] = useState(false);
  const [navExpandedByUser, setNavExpandedByUser] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768
  );
  const [activeTab, setActiveTab] = useState('story');

  // Handle browser back/forward and path updates
  const handleLocationChange = useCallback(() => {
    setCurrentRoute(getInitialRoute());
  }, []);

  useEffect(() => {
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, [handleLocationChange]);

  const navigateTo = (newPath) => {
    if (typeof window !== 'undefined') {
      window.history.pushState(null, '', newPath);
      setCurrentRoute(getInitialRoute());
    }
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const isScrolled = currentY >= 60;
      setNavScrolled(isScrolled);
      setShowScrollTop(currentY > 400);

      // Auto-collapse when scrolling back up to absolute top
      if (!isScrolled) {
        setNavExpandedByUser(false);
      }

      // Update active bottom tab based on scroll position
      const sections = [
        { id: 'section-hero', tab: 'story' },
        { id: 'section-projects', tab: 'projects' },
        { id: 'section-personal', tab: 'personal' },
        { id: 'section-contact', tab: 'contact' },
      ];
      let current = 'story';
      for (const s of sections) {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top <= window.innerHeight * 0.6) {
          current = s.tab;
        }
      }
      setActiveTab(current);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track viewport width
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll to section by id
  const scrollToSection = (id, tab) => {
    if (tab) setActiveTab(tab);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // On desktop — collapse when scrolled & user hasn't expanded
  const isCollapsed = navScrolled && !navExpandedByUser;

  // If visiting /sudoku directly or navigated there
  if (currentRoute === 'sudoku') {
    return <SudokuPage onNavigateHome={() => navigateTo('/')} />;
  }

  return (
    <div className="App">

      {/* ─── TOP NAVBAR — desktop only (hidden on mobile ≤768px) ─── */}
      {!isMobile && (
        <nav
          className={`navbar${navScrolled ? ' navbar--scrolled' : ''}${isCollapsed ? ' navbar--collapsed' : ' navbar--expanded'}`}
          onClick={isCollapsed ? () => setNavExpandedByUser(true) : undefined}
        >
          <div className="nav-container">
            <div
              className="logo"
              onClick={(e) => {
                if (!isCollapsed) {
                  e.stopPropagation();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              <FiTerminal className="logo-icon" />
              <span className="logo-text"><span className="logo-gold">Shawaiz</span> Ali</span>
            </div>

            <div className="nav-right-wrapper">
              {/* Expanded items */}
              <div className={`nav-right-group${isCollapsed ? ' nav-right-group--hidden' : ''}`}>
                <ul className="nav-menu">
                  <li><button onClick={(e) => { e.stopPropagation(); scrollToSection('section-hero'); }}>Story</button></li>
                  <li><button onClick={(e) => { e.stopPropagation(); scrollToSection('section-projects'); }}>Projects</button></li>
                  <li><button onClick={(e) => { e.stopPropagation(); scrollToSection('section-personal'); }}>Personal</button></li>
                  <li><button onClick={(e) => { e.stopPropagation(); scrollToSection('section-contact'); }}>Contact</button></li>
                </ul>
                <a
                  href="/resume.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nav-resume-btn font-mono"
                  onClick={(e) => e.stopPropagation()}
                >
                  <FiDownload />
                  <span>Resume</span>
                </a>
                {navScrolled && (
                  <button
                    className="nav-close-btn"
                    onClick={(e) => { e.stopPropagation(); setNavExpandedByUser(false); }}
                    title="Collapse Menu"
                  >
                    <FiX />
                  </button>
                )}
              </div>

              {/* Collapsed MENU button */}
              <button
                className={`nav-collapsed-btn font-mono${isCollapsed ? ' nav-collapsed-btn--visible' : ''}`}
                onClick={(e) => { e.stopPropagation(); setNavExpandedByUser(true); }}
                title="Expand Menu"
              >
                <span>MENU</span>
                <FiMenu className="nav-menu-icon" />
              </button>
            </div>
          </div>
        </nav>
      )}

      {/* ─── MOBILE TOP BAR — clean minimal logo + CV link ─── */}
      {isMobile && (
        <div className="mobile-topbar">
          <div className="mobile-topbar-inner">
            <div
              className="mobile-logo"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <FiTerminal className="logo-icon" />
              <span className="logo-text"><span className="logo-gold">Shawaiz</span> Ali</span>
            </div>
            <a
              href="/resume.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="mobile-resume-btn font-mono"
            >
              <FiDownload size={14} />
              <span>CV</span>
            </a>
          </div>
        </div>
      )}

      <main className="main-content--cinematic">
        <StoryContainer theme="dark" onNavigateToSudoku={() => navigateTo('/sudoku')} />
      </main>

      {/* ─── MOBILE BOTTOM TAB BAR — native app feel ─── */}
      {isMobile && (
        <nav className="mobile-bottom-nav" aria-label="Mobile Navigation">
          <button
            className={`mobile-tab${activeTab === 'story' ? ' mobile-tab--active' : ''}`}
            onClick={() => scrollToSection('section-hero', 'story')}
          >
            <FiBookOpen className="mobile-tab-icon" />
            <span className="mobile-tab-label">Story</span>
          </button>
          <button
            className={`mobile-tab${activeTab === 'projects' ? ' mobile-tab--active' : ''}`}
            onClick={() => scrollToSection('section-projects', 'projects')}
          >
            <FiCode className="mobile-tab-icon" />
            <span className="mobile-tab-label">Projects</span>
          </button>
          <button
            className={`mobile-tab${activeTab === 'personal' ? ' mobile-tab--active' : ''}`}
            onClick={() => scrollToSection('section-personal', 'personal')}
          >
            <FiHeart className="mobile-tab-icon" />
            <span className="mobile-tab-label">Personal</span>
          </button>
          <button
            className={`mobile-tab${activeTab === 'contact' ? ' mobile-tab--active' : ''}`}
            onClick={() => scrollToSection('section-contact', 'contact')}
          >
            <FiMail className="mobile-tab-icon" />
            <span className="mobile-tab-label">Contact</span>
          </button>
        </nav>
      )}

      {/* Floating Scroll to Top — desktop only */}
      {showScrollTop && !isMobile && (
        <button
          className={`scroll-to-top-btn font-mono${showScrollTop ? ' visible' : ''}`}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          title="Scroll to top"
          aria-label="Scroll to top"
        >
          <FiChevronUp className="scroll-top-icon" />
          <span className="scroll-top-text">TOP</span>
        </button>
      )}

      <footer className="footer">
        <p>© 2026 Shawaiz Ali Rehman — Mobile &amp; Software Engineer</p>
      </footer>
    </div>
  );
}

export default App;
